import { createSelector } from '@reduxjs/toolkit';
import { isEmpty } from 'ramda';

import { selectCartItemsLength, selectPromoFreeActive } from '../cart/selectors';
import ooeConstants from '../constants';
import { selectCardSelected, selectGuestDetailsValid, selectGuestFormSyncErrors } from '../guest/selectors';
import {
  selectDeliveryTip,
  selectDestination,
  selectDestinationValid,
  selectDetailsErrors,
  selectDetailsFormSyncErrors,
  selectDetailsFormValues,
  selectPaperGoodsError,
  selectPaperGoodsOptions,
  selectSelectedPaymentMethod,
  selectSmsConsent,
  selectTotalAmount,
} from '../order/selectors';
import { selectEnrolledInDms } from '../user/selectors';
import { submitWarningErrorMessages } from '../util/customerErrorMessages';
import { getVariation } from '../services/launchDarkly';
import { capitalizeFirstLetter, formatPaperGoodsOptions } from '../util/format';

export const selectDisplayDeliveryTip = createSelector(
  selectDeliveryTip,
  (deliveryTip) => deliveryTip && 'tipAmount' in deliveryTip,
);

export const selectPaymentValid = createSelector(
  selectCardSelected,
  selectSelectedPaymentMethod,
  (card, selectedPaymentMethod) => {
    if (selectedPaymentMethod === ooeConstants.CREDIT) {
      return !isEmpty(card) && card !== 'error';
    }
    return true;
  },
);

export const selectPayWithVaultedCard = createSelector(
  selectSelectedPaymentMethod,
  selectPaymentValid,
  (selectedPaymentMethod, paymentValid) => selectedPaymentMethod === ooeConstants.CREDIT && paymentValid,
);

export const selectEventDetailsValid = createSelector(
  selectDetailsErrors,
  selectPaperGoodsError,
  (detailsErrors, paperGoodsError) => {
    const hasErrors = detailsErrors && Object.keys(detailsErrors).length > 0;
    return !hasErrors && paperGoodsError.length === 0;
  },
);

export const selectEventSummaryValid = createSelector(
  selectEventDetailsValid,
  selectGuestDetailsValid,
  selectDestinationValid,
  (detailsValid, guestValid, destinationValid) => detailsValid && guestValid && destinationValid,
);

export const selectAllFormsValid = createSelector(
  selectEventSummaryValid,
  selectCartItemsLength,
  selectPaymentValid,
  (detailsAndGuest, cartLength, paymentValid) => paymentValid && detailsAndGuest && cartLength > 0,
);

export const selectFormSyncErrors = createSelector(
  selectGuestFormSyncErrors,
  selectDetailsFormSyncErrors,
  selectPaperGoodsError,
  (guestFormSyncErrors, detailsFormSyncErrors, paperGoodsError) => {
    const errorsArray = [...guestFormSyncErrors, ...detailsFormSyncErrors, ...paperGoodsError].map(
      capitalizeFirstLetter,
    );
    const { length: errorsArrayLen } = errorsArray;
    return errorsArray.map((fieldName, idx) =>
      errorsArrayLen > 2 && idx === errorsArrayLen - 1 ? `and ${fieldName}` : fieldName,
    );
  },
);

export const selectFormSubmitWarning = createSelector(selectFormSyncErrors, (errors) => {
  if (errors.length === 0) {
    return {};
  }
  const prepositionToUse = errors.length === 1 ? 'is a' : 'are';
  const pluralizingLetter = errors.length === 1 ? '' : 's';
  return {
    type: 'error',
    message: `${errors.join(', ')} ${prepositionToUse} required field${pluralizingLetter}.`,
  };
});

export const selectPaymentSubmitWarning = createSelector(selectPaymentValid, (paymentValid) => {
  if (!paymentValid) {
    return {
      type: 'error',
      message: 'Payment method must be validated by entering the zip code associated with the selected card.',
    };
  }
  return {};
});

export const selectAllItemsPromoFree = createSelector(
  selectPromoFreeActive,
  selectTotalAmount,
  (promoFreeActive, totalAmount) => promoFreeActive && totalAmount === 0,
);

export const selectSpecialInstructionMessage = createSelector(
  selectPaperGoodsOptions,
  selectDetailsFormValues,
  (paperGoods, details) => {
    if (getVariation('showPaperGoods', false)) {
      return (details?.specialInstructions || '').trim();
    }
    return `${formatPaperGoodsOptions(paperGoods)} ${details?.specialInstructions || ''}`;
  },
);

export const selectIsSmsConsentMissingForDelivery = createSelector(
  selectSmsConsent,
  selectDestination,
  selectEnrolledInDms,
  (smsConsent, destination, enrolledInDms) =>
    smsConsent === null && destination === ooeConstants.DELIVERY && enrolledInDms,
);

export const selectSmsConsentWarning = createSelector(
  selectIsSmsConsentMissingForDelivery,
  (smsConsentMissing) =>
    smsConsentMissing
      ? {
          type: 'error',
          message: submitWarningErrorMessages.SMS_CONSENT_REQUIRED,
        }
      : {},
);
